import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../utils/api";
import { Validation } from "../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../@types/snackbarAPI.types";
import useRouteParam from "../../../../hooks/useRouteParam";
import { Group } from "../../../../@types/group.types";
import { useEmployerRoute } from "../../../../hooks";
import ROLES from "../../../../constants/roles";
import validateEmail from "../../../../utils/commonFunctions/validateEmail";

const chipMessage = "This functionality will update the Email of Employee/Dependent of specific group.";

const EmailUpdation = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		group_number: null,
		role: "",
		SSN: "",
		email: ""
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, role, email, SSN } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (role.length === 0) {
			_validation.current.reportFieldDetails["role"] = "Role is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (email.length === 0) {
			_validation.current.reportFieldDetails["email"] = "Email is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (email.length !== 0) {
			if (!validateEmail(email)) {
				_validation.current.reportFieldDetails["email"] = "Valid Email is required";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}
		if (SSN.length === 0) {
			_validation.current.reportFieldDetails["SSN"] = "SSN is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// // console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name } = event.target;
			const { value } = event.target;

			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Member Email Updation");
					const { group_number, role, SSN, email } = reportFieldDetails;

					const payload = {
						group_number,
						role,
						SSN: SSN.replaceAll("-", ""),
						email
					};

					// console.log("Payload", payload);
					const response: any = await trackPromise(api.memberInfoUpdate.emailUpdate(payload));

					if (response) {
						// console.log("Response", response);
						alert("Member Email Updation Successful");
						window.location.reload();

						// setTimeout(() => {
						// 	setSnackbarAPICallProps(
						// 		Object.assign({}, snackbarAPICallProps, {
						// 			open: true,
						// 			message: "Member Email Updation Successful!",
						// 			severity: "success"
						// 		})
						// 	);
						// }, 2000);
					}
				}
			} catch (error) {
				alert("Member Email Updation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.member_email_updation);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Member Email Updation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Member Role",
							name: "role",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.role,
							type: "select",
							options: [ROLES.employee, ROLES.dependent]
						},
						{
							label: "Member SSN",
							name: "SSN",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Member SSN",
							value: reportFieldDetails.SSN,
							type: "textfield"
						},
						{
							label: "New Email",
							name: "email",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "New Email",
							value: reportFieldDetails.email,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [assignedGroups, reportFieldDetails, handleChange, isEmployerRoute, queryGroupNumber, selectedGroup?.name]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				// selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={() => null}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default EmailUpdation;
